import { isAxiosError } from 'axios'

import { computed, ref } from 'vue'
import type { Ref } from 'vue'

import * as Sentry from '@sentry/vue'

import { initializeApp } from 'firebase/app'
import type { FirebaseApp } from 'firebase/app'
import {
  OAuthProvider,
  SAMLAuthProvider,
  getAuth,
  getIdToken,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'
import type { IdTokenResult, Unsubscribe, User } from 'firebase/auth'
import { doc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore'
import { getBlob, getStorage, ref as storageRef } from 'firebase/storage'

import type { MeAccountInfo } from '@jouzen/outo-api-client-ts'

import { handleRequestError } from '#web-oura-tools/errors'

import { useAxios } from '#vue-shared-lib/composables/axios'

import { initPrefs } from '#web-oura-tools/prefs/utilities'

import { ApiProviders } from '#web-oura-tools/types/auth'
import type {
  AppEnvironment,
  AuthConfig,
  CloudEnvironment,
  CustomClaims,
  TokenDetails,
  UserDetails,
} from '#web-oura-tools/types/auth'
import type { RequestError } from '#web-oura-tools/types/errors'

import { OuraUser } from '#web-oura-tools/auth/oura-user'
import { UserPrefs } from '#web-oura-tools/prefs/user-prefs'

const ENV: AppEnvironment = import.meta.env.VITE_APP_ENV

const REQUEST_SOURCE_GET_CURRENT_USER = 'getCurrentUser'

const SESSION_DURATION = 12 * 60 * 60 * 1000 // 12 hours in ms

const firebaseApps = {
  sandbox: !ENV
    ? initializeApp({
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      })
    : initializeApp(
        {
          appId: import.meta.env.VITE_FIREBASE_APP_ID_SANDBOX,
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY_SANDBOX,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_SANDBOX,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_SANDBOX,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_SANDBOX,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_SANDBOX,
        },
        'sandbox',
      ),
  staging:
    ENV !== 'sandbox'
      ? initializeApp(
          {
            appId: import.meta.env.VITE_FIREBASE_APP_ID_STAGING,
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY_STAGING,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_STAGING,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_STAGING,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_STAGING,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_STAGING,
          },
          'staging',
        )
      : null,
  release:
    ENV === 'release'
      ? initializeApp(
          {
            appId: import.meta.env.VITE_FIREBASE_APP_ID_RELEASE,
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY_RELEASE,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_RELEASE,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_RELEASE,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_RELEASE,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_RELEASE,
          },
          'release',
        )
      : null,
}

// Old Waltari GCP project, initialized on first use (see authWaltariProject)
let waltariFirebaseApp: FirebaseApp | null = null

let embeddedBrowserNoticeShown = false

const authenticatedEnvs: string[] = [ENV]
const environmentAuthInProgress = new Map<AppEnvironment, Promise<void>>()

let authConfig: AuthConfig | undefined = undefined

let authStateUnsubscribe: Unsubscribe | null = null

const authError: Ref<RequestError | null> = ref(null)
const sessionExpiresAt: Ref<number | null> = ref(null)

let authReady: Promise<boolean> = Promise.resolve(false)

const user: Ref<OuraUser | null | undefined> = ref(undefined)
const prefs: Ref<UserPrefs | null | undefined> = ref(undefined)

const appEnv: Ref<AppEnvironment> = ref(import.meta.env.VITE_APP_ENV)
const cloudEnv: Ref<CloudEnvironment> = ref(import.meta.env.VITE_SYS_ENV)

const firebaseApp = computed(() => firebaseApps[ENV] ?? firebaseApps.sandbox)

const firebaseStorage = computed(() => getStorage(firebaseApps[appEnv.value] ?? firebaseApp.value))

/*
 * SAML sign-in can't complete inside embedded browsers (e.g. Cursor / VS Code):
 * Detect those and show a full-screen notice instead of attempting to log in.
 */
function isEmbeddedBrowser() {
  return /Electron/i.test(navigator.userAgent)
}

function showEmbeddedBrowserNotice() {
  if (embeddedBrowserNoticeShown) {
    return
  }

  embeddedBrowserNoticeShown = true

  const url = window.location.href

  const overlay = document.createElement('div')
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;' +
    'padding:24px;background:#0f172a;color:#e2e8f0;font-family:system-ui,-apple-system,sans-serif;'

  const card = document.createElement('div')
  card.style.cssText = 'max-width:520px;text-align:center;'

  const title = document.createElement('h1')
  title.textContent = 'Open this app in your browser'
  title.style.cssText = 'font-size:20px;font-weight:600;margin:0 0 12px;'

  const body = document.createElement('p')
  // textContent (not innerHTML) so the URL below can never inject markup.
  body.textContent =
    "OuraHub Sign-in can't complete inside the Cursor / VS Code built-in browser. Copy the link below and open it in Chrome to log in."
  body.style.cssText = 'margin:0 0 16px;line-height:1.5;color:#94a3b8;'

  const link = document.createElement('code')
  link.textContent = url
  link.style.cssText = 'display:block;padding:10px 12px;border-radius:8px;background:#1e293b;word-break:break-all;'

  const copyButton = document.createElement('button')
  copyButton.type = 'button'
  copyButton.textContent = 'Copy link'
  copyButton.style.cssText =
    'margin-top:16px;padding:8px 16px;border:0;border-radius:8px;cursor:pointer;' +
    'background:#38bdf8;color:#0f172a;font-weight:600;'
  copyButton.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(url)

      copyButton.textContent = 'Copied!'
    } catch {
      copyButton.textContent = 'Copy failed'
    }
  })

  card.append(title, body, link, copyButton)
  overlay.append(card)
  document.body.append(overlay)
}

function useAuthStore(config?: AuthConfig) {
  let logoutTimeout: number | undefined = undefined

  /*
   * Initializes the auth listener.
   */
  if (config) {
    authConfig = config
  }

  if (!authStateUnsubscribe) {
    authReady = subscribeAuthStateChanged()

    console.debug('Auth initialized with config', authConfig)
  }

  function subscribeAuthStateChanged() {
    const ready = new Promise<boolean>((resolve, reject) => {
      if (authStateUnsubscribe) {
        authStateUnsubscribe()
      }

      authStateUnsubscribe = onAuthStateChanged(getAuth(firebaseApp.value), async (u: User | null) => {
        const currentUser = user.value

        console.log('Auth state changed', u)

        try {
          if (u) {
            if (currentUser?.id !== u.uid) {
              const tokenDetails = await getTokenDetails(u)

              console.debug('Auth token-details', tokenDetails)

              if (tokenDetails.isSessionExpired) {
                console.info('Auth session was expired')

                await invalidUserOrSession(true)
              } else {
                await userWasChanged(tokenDetails)
              }
            }
          } else {
            await invalidUserOrSession(false)

            user.value = null
          }

          resolve(true)
        } catch (error) {
          console.error('Auth state handling failed', error)

          Sentry.captureException(error)

          user.value = null

          reject(error)
        }
      })
    })

    // Mark handled so apps that never await authReady don't get an unhandled rejection; awaiters still see it.
    ready.catch(() => undefined)

    return ready
  }

  async function getTokenDetails(u: User) {
    const uid = u.uid
    const email = u.email!

    const idToken = await u.getIdTokenResult()

    const groups = parseGroups(idToken)
    const audience = parseAudience(idToken)

    const authTimeInMillis = Number(idToken.claims.auth_time) * 1000
    const expiryTimeInMillis = SESSION_DURATION - (Date.now() - authTimeInMillis)

    const isSessionExpired = expiryTimeInMillis <= 0

    return {
      uid,
      email,
      groups,
      audience,
      isSessionExpired,
      authTimeInMillis,
      expiryTimeInMillis,
    }
  }

  async function userWasChanged(tokenDetails: TokenDetails) {
    // User data needs to be updated before setting the current user or cached data may be served!
    await updateUserData(tokenDetails)
    await setCurrentUser(tokenDetails)

    scheduleLogoutTimer(tokenDetails.expiryTimeInMillis)
  }

  /*
   * Signs in to the old Waltari GCP project with the current OuraHub identity and returns its Firebase app.
   * Temporary solution to use projects data from the old Waltari GCP project before migrating completely to
   * oura-tools.
   */
  async function authWaltariProject(): Promise<FirebaseApp> {
    if (waltariFirebaseApp) {
      return waltariFirebaseApp
    }

    await authReady

    if (ENV === 'staging') {
      await authOtherEnvironment('sandbox')
    }

    // The oidc.ourahub provider in each Waltari project trusts a single oura-tools issuer: oura-waltari-release
    // trusts oura-tools-release and oura-waltari-sandbox trusts oura-tools-sandbox. Staging builds target the
    // Waltari sandbox, so they use the sandbox token and needs to be logged into above before proceeding.
    const sourceApp = ENV === 'release' ? firebaseApps.release : firebaseApps.sandbox

    const sourceUser = sourceApp ? getAuth(sourceApp).currentUser : null

    if (!sourceUser) {
      throw new Error("Can't authenticate to the Waltari project without a signed in OuraHub user")
    }

    waltariFirebaseApp = initializeApp(
      {
        appId: import.meta.env.VITE_FIREBASE_APP_ID_WALTARI,
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY_WALTARI,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_WALTARI,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_WALTARI,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_WALTARI,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_WALTARI,
      },
      'waltari',
    )

    const provider = new OAuthProvider('oidc.ourahub')

    const credential = provider.credential({
      idToken: await getIdToken(sourceUser),
    })

    await signInWithCredential(getAuth(waltariFirebaseApp), credential)

    return waltariFirebaseApp
  }

  async function authOtherEnvironment(env: AppEnvironment) {
    const targetApp = firebaseApps[env]

    if (
      targetApp &&
      !authenticatedEnvs.includes(env) &&
      ((ENV !== 'sandbox' && env === 'sandbox') || (ENV === 'release' && env === 'staging'))
    ) {
      const inProgress = environmentAuthInProgress.get(env)

      if (inProgress) {
        await inProgress
        return
      }

      const authentication = (async () => {
        console.log(`Authenticating other env: ${env}`)

        const provider = new OAuthProvider(`oidc.${ENV}`)

        const idToken = await getIdToken(getAuth(firebaseApp.value).currentUser!)

        const credential = provider.credential({ idToken })

        await signInWithCredential(getAuth(targetApp), credential)

        authenticatedEnvs.push(env)
      })()

      environmentAuthInProgress.set(env, authentication)

      try {
        await authentication
      } finally {
        environmentAuthInProgress.delete(env)
      }
    }
  }

  async function invalidUserOrSession(isSessionExpired: boolean) {
    if (
      (authConfig?.enableAutoLogin &&
        (window.location.pathname !== '/' || // Hash routing
          window.location.hash?.includes('/'))) || // Normal routing
      window.location.host.startsWith('localhost') || // Local development
      window.location.host.includes('.shared.ourahub') || // Standalone ourahub site
      window.location.host.match('--.*.web.app') // Firebase hosting preview channel
    ) {
      await login()
    } else if (isSessionExpired) {
      await logout()
    }
  }

  /*
   * Performs user login procedure.
   */
  async function login() {
    console.debug('Initiate login')

    if (isEmbeddedBrowser()) {
      console.warn('Login unavailable in embedded browser, showing notice')

      showEmbeddedBrowserNotice()

      return
    }

    const provider = new SAMLAuthProvider('saml.oura')

    const usePopup = window.location.host !== import.meta.env.VITE_FIREBASE_AUTH_DOMAIN

    if (!usePopup) {
      await signInWithRedirect(getAuth(firebaseApp.value), provider)
    } else {
      try {
        if (authStateUnsubscribe) {
          // Clean up the auth listener before reloading to avoid errors,
          // the new app instance will establish its own listener again.
          authStateUnsubscribe()
        }

        await signInWithPopup(getAuth(firebaseApp.value), provider)

        window.location.reload()
      } catch (error) {
        console.error('Popup closed or blocked, login interrupted', error)

        if (authConfig?.enableAutoLogin && window.location.pathname !== '/') {
          console.debug('Location reset due to login error occurred')

          window.history.pushState({}, '', '/')
        }
      }
    }
  }

  /*
   * Performs user logout procedure.
   */
  async function logout() {
    user.value = null

    console.debug('Auth signing out from Firebase')

    await signOut(getAuth(firebaseApp.value))

    if (ENV !== 'sandbox' && firebaseApps.sandbox) {
      await signOut(getAuth(firebaseApps.sandbox))
    }

    if (ENV === 'release' && firebaseApps.staging) {
      await signOut(getAuth(firebaseApps.staging))
    }

    if (waltariFirebaseApp) {
      await signOut(getAuth(waltariFirebaseApp))
    }

    if (window.location.pathname !== '/') {
      console.debug('Location reset due to logout action requested')

      window.history.pushState({}, '', '/')
    }
  }

  /*
   * Returns the firebase auth token.
   */

  async function authToken(env?: AppEnvironment) {
    if (env && env !== ENV) {
      await authOtherEnvironment(env)
    }

    const fbApp = (env && firebaseApps[env]) ?? firebaseApp.value

    const currentUser = getAuth(fbApp).currentUser

    return currentUser ? getIdToken(currentUser) : undefined
  }

  /*
   * Toggles permissions temporally on / off.
   */

  function togglePermission(permission: string) {
    const overrides: string[] = sessionStorage.OuraOverrides?.split(' ') ?? []

    if (overrides.includes(permission)) {
      sessionStorage.OuraOverrides = overrides.filter((override) => override !== permission).join(' ')
    } else {
      sessionStorage.OuraOverrides = [...overrides, permission].join(' ')
    }

    window.location.reload()
  }

  /*
   * Toggles the OuraHub access-rights bypass on / off (for bypass-eligible users).
   */

  function toggleAdminBypass() {
    if (sessionStorage.OuraBypassDisabled) {
      delete sessionStorage.OuraBypassDisabled
    } else {
      sessionStorage.OuraBypassDisabled = 'true'
    }

    window.location.reload()
  }

  /*
   * Changes authentication to another env.
   */

  async function changeEnvironment(env: AppEnvironment) {
    appEnv.value = env

    if (env && env !== ENV) {
      await authOtherEnvironment(env)
    }

    console.log('Changing environment:', appEnv.value)

    const fbApp = firebaseApps[appEnv.value] ?? firebaseApp.value

    const currentUser = getAuth(fbApp).currentUser

    const _tokenDetails = await getTokenDetails(currentUser!)

    /* await setCurrentUser({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(tokenDetails as any),
      uid: user.value!.id,
      groups: user.value!.groups,
      audience: user.value!.audience,
    }) */
  }

  async function downloadFile(path: string) {
    const fileRef = storageRef(firebaseStorage.value, path)

    const blob = await getBlob(fileRef)

    return blob
  }

  /*
   * Internal helper functions for the auth.
   */

  function parseGroups(idToken: IdTokenResult): string[] {
    const claims = idToken.claims as CustomClaims

    if (claims.firebase?.sign_in_attributes?.groups) {
      const groups = Array.isArray(claims.firebase?.sign_in_attributes?.groups)
        ? claims.firebase?.sign_in_attributes?.groups
        : [claims.firebase?.sign_in_attributes?.groups]

      return groups
    } else {
      return []
    }
  }

  function parseAudience(idToken: IdTokenResult): string {
    const claims = idToken.claims as CustomClaims

    return claims.aud ?? ''
  }

  /*
   * Updates user login info into firestore database.
   */
  async function updateUserData(userDetails: UserDetails) {
    const firestore = getFirestore(firebaseApp.value)

    const docRef = doc(firestore, 'account-data', userDetails.uid)

    console.debug('Updating database info:', userDetails, firebaseApp.value)

    await setDoc(
      docRef,
      {
        email: userDetails.email,
        groups: user.value?.groups ?? [],
        rights: user.value?.rights ?? [],
        lastActive: serverTimestamp(),
      },
      { merge: true },
    )
  }

  async function setCurrentUser(userDetails: UserDetails) {
    authError.value = null

    let rights: string[] = []

    try {
      rights = await applyApiProviders(authConfig?.apiProviders ?? [])
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        authError.value = await handleRequestError(error, REQUEST_SOURCE_GET_CURRENT_USER)
      } else {
        throw new Error('Unknown error occurred while getting providers', { cause: error })
      }
    }

    console.info('Updating current user:', userDetails.email, userDetails.audience, userDetails.groups, rights)

    // Ensure prefs is also available for the user, not sure if this is needed though...
    await initPrefs(firebaseApp.value, userDetails.uid)

    user.value = new OuraUser(userDetails.uid, userDetails.email, userDetails.audience, userDetails.groups, rights)

    if (authConfig?.userPreferencesKey) {
      prefs.value = new UserPrefs(firebaseApp.value, userDetails.uid, authConfig.userPreferencesKey)

      await prefs.value.load()
    }
  }

  async function applyApiProviders(providers: ApiProviders[]) {
    const { axiosClient } = useAxios({
      agent: 'ourahub-intra',

      url: import.meta.env.VITE_OUTO_API_URL,

      version: import.meta.env.VITE_APP_VERSION,
    })

    const rights = await Promise.all(
      providers.map(async (provider) => {
        switch (provider) {
          case ApiProviders.OUTO_CLOUD: {
            const { data } = await axiosClient.get<MeAccountInfo>(`/api/v1/me`)

            return data?.rights ?? []
          }

          default:
            return []
        }
      }),
    )
    return rights.flat()
  }

  function scheduleLogoutTimer(millisecondsUntilExpiration: number) {
    sessionExpiresAt.value = millisecondsUntilExpiration + Date.now()

    if (logoutTimeout) {
      window.clearTimeout(logoutTimeout)
    }

    logoutTimeout = window.setTimeout(() => logout(), millisecondsUntilExpiration)

    console.log('Scheduled logout at', new Date(sessionExpiresAt.value).toISOString())
  }

  return {
    user,
    prefs, // To be deprecated
    login,
    logout,
    appEnv,
    cloudEnv,
    authError,
    authReady,
    authToken,
    authWaltariProject, // To be deprecated
    firebaseApp,
    firebaseApps,
    firebaseStorage, // To be deprecated
    sessionExpiresAt,
    downloadFile, // To be deprecated
    togglePermission,
    toggleAdminBypass,
    changeEnvironment,
  }
}

export default useAuthStore
